

import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import DataTable from "../DataTable.vue"

import User from "@/types/user"
import { TemplateDetails, Field, Template } from "@/types"
import { CaseField } from "@/types/caseField"

import DataForm from "../DataForm.vue"
import CalculatedField from "./CalculatedField.vue"
import FloatingPanel from "../../Controls/FloatingPanel.vue"
import SearchInput from "../../Controls/SearchInput.vue"

import api from "@/services/api"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-form": DataForm,
		"floating-panel": FloatingPanel,
		"search-input": SearchInput
	}
})
export default class FieldSelector extends Vue {
	@Prop()
	public show!: boolean;

	@Prop()
	public fields!: Field[];

	@Prop({ default: () => { return [] } })
	public allowedFieldTypes!: string[];

	@Prop({ default: true })
	public showPermissions!: boolean;

	@Prop()
	public addFieldsToSection!: string;

	@setup.Getter("getFieldTypes")
	fieldTypes!: [];

	@setup.Getter("getDataSources")
	dataSources!: any[];

	@setup.Getter("getDefinitions")
	definitions!: [];

	@setup.Getter("getDataSourcesById")
	dataSourceDictionary!: { [key: string]: any };

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	public valid: boolean = false;

	public get columns (): any[] {
		var columns: {
			key: string,
			label?: string | null,
			sortable?: boolean,
			dataType?: string,
			valueColumn?: string,
			options?: any[],
			hasTemplate?: boolean,
			dynamicComponent?: any,
			inTable?: boolean,
			limitOptions?: boolean,
			inModal?: (item: any) => boolean,
			errors?: (item: any) => string | null
		}[] = [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "description",
				label: "Description",
				sortable: true
			},
			{
				key: "dataTypeTitle",
				label: "Data Type",
				sortable: true,
				dataType: "list",
				valueColumn: "dataType",
				options: this.fieldTypes.filter((c: any) =>
					this.allowedFieldTypes.length === 0 ||
					this.allowedFieldTypes.includes(c.id)),
				errors: (item: any) => item.dataType ? null : "Please select a data type"
			},
			{
				key: "dataSourceTitle",
				label: "Data Source",
				dataType: "list",
				valueColumn: "dataSourceId",
				options: this.dataSources,
				sortable: true,
				limitOptions: true,
				inModal: (item: any) => item.dataType === "list" || item.dataType === "list-data" || item.dataType === "list-multiple",
				errors: (item: any) => item.dataType === "list" && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "dataDefinitionTitle",
				label: "Data Definition",
				dataType: "list",
				valueColumn: "definitionId",
				options: this.definitions,
				sortable: true,
				inModal: (item: any) => item.dataType === "list-data",
				errors: (item: any) => item.dataType === "list-data" && !item.definitionId && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "extensions",
				label: null,
				inModal: (item: any) => item.dataType === "calculated" || item.dataType === "user-role",
				dynamicComponent: CalculatedField,
				inTable: false
			},
			{
				key: "allowFiles",
				label: "Allow Files",
				sortable: true,
				hasTemplate: true,
				dataType: "checkbox"
			}
		]

		if (this.showPermissions) {
			columns.push(
				{
					key: "permissions.record",
					label: this.getRecordName + " Permissions",
					hasTemplate: true,
					dataType: "permissions"
				})
			columns.push({
				key: "permissions.case",
				label: this.getCaseName + " Permissions",
				hasTemplate: true,
				dataType: "permissions"
			})
		}

		return columns
	}

	public newField: any = null;

	public creatingNewField: boolean = false;
	public fieldFilter: string = "";

	public requiredFieldIds: string[] = [];

	@Watch("show")
	onShowChanged (val: any) {
		// Start each open with a clear filter, and focus the search box.
		if (val) {
			this.fieldFilter = ""
			this.$nextTick(() => {
				const search = this.$refs.search as any
				if (search && typeof search.focus === "function") search.focus()
			})
		}
	}

	public get displayFields () {
		this.calculateRequiredFields()

		if (!this.fieldFilter) { return this.fields.filter(f => !f.added && f.title.indexOf(this.fieldFilter) > -1) }

		const lowerFieldFilter = this.fieldFilter.toLowerCase()
		return this.fields.filter(f => !f.added && f.title.toLowerCase().indexOf(lowerFieldFilter) > -1)
	}

	public calculateRequiredFields () {
		var avaliableFields = this.fields.filter(f => !f.added)

		// Set fields to be Automatically added if they're required by another field
		avaliableFields.filter(f => f.add && f.extensions && f.extensions.fields).forEach(field => {
			// Loop through the extensions to find fields that the current field requires
			field.extensions!.fields.forEach((fieldId:string) => {
				var dependedUponField = avaliableFields.find(f => f.id === fieldId)
				// Set the field to be automatically added
				if (dependedUponField && !this.requiredFieldIds.find(id => id === fieldId)) {
					this.requiredFieldIds.push(fieldId)
				}
			})
		})

		// unset requiredFields if they're not required by another field
		this.requiredFieldIds.forEach((id, index) => {
			var fieldRequired = false
			// Check if the field is required by another Field
			avaliableFields.filter(f => f.add && f.extensions).forEach(f => {
				if (f.extensions!.fields.find((fieldId: String) => fieldId === id)) {
					fieldRequired = true
				}
			})

			// remove from requiredFieldIds if it is not required by another field
			if (!fieldRequired) this.requiredFieldIds[index] = ""
		})
		this.requiredFieldIds = this.requiredFieldIds.filter(id => id !== "")
	}

	public fieldRequired (field: Field) {
		return this.requiredFieldIds.find(id => id === field.id)
	}

	// Fields that would be added: checked ones plus any auto-required, deduped.
	public get selectedCount (): number {
		const ids = new Set(this.fields.filter(f => f.add).map(f => f.id))
		this.requiredFieldIds.forEach(id => ids.add(id))
		return ids.size
	}

	// Toggle selection when the row is clicked (required fields are forced on).
	public toggleField (field: any) {
		if (this.fieldRequired(field)) return
		this.$set(field, "add", !field.add)
	}

	addFields () {
		const addFields = this.fields.filter(f => f.add)

		// Add autoAddFields
		this.requiredFieldIds.forEach(id => {
			var field = this.fields.find(f => f.id === id)
			if (field) {
				field.add = true
				addFields.push(field)
			}
		})
		this.requiredFieldIds = []

		this.cancelSelect()

		this.$emit("addFields", addFields)
	}

	cancelSelect () {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "add", false)
		})
	}

	@Emit("cancel")
	cancelAdd () {
		this.cancelSelect()
	}

	createNewField () {
		this.newField = { permissions: { record: { view: [], edit: [] }, case: { view: [], edit: [] } } }
		this.creatingNewField = true
	}

	async saveNewField () {
		if (this.valid) {
			if (this.newField.dataType === "calculated") {
				CalculatedField.stripUnusedOptions(this.newField)
			}

			const fieldId = await api.fieldSave(this.newField)
			this.$emit("addNewField", fieldId)
			this.creatingNewField = false
		}
	}

	cancelNewField () {
		this.creatingNewField = false
	}
}

